import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  DocumentMagnifyingGlassIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import TransactionHistoryReport from './TransactionHistoryReport';
import { fetchJson } from '../utils/http';
import TransactionImageField from './TransactionImageField';
import { fetchTransactionPhotoRequired, imagePayload } from '../utils/transactionPhoto';
import TransactionProofImage from './TransactionProofImage';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const roleStyles = {
  company_owner: 'bg-violet-100 text-violet-700 border-violet-200',
  admin: 'bg-sky-100 text-sky-700 border-sky-200',
  worker: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const typeStyles = {
  credit: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  debit: 'bg-rose-100 text-rose-700 border-rose-200',
  return: 'bg-amber-100 text-amber-700 border-amber-200',
};

const statusStyles = {
  SAVE: 'bg-amber-100 text-amber-700 border-amber-200',
  SUBMIT: 'bg-blue-100 text-blue-700 border-blue-200',
  RECEIVED: 'bg-slate-100 text-slate-700 border-slate-200',
  RETURN: 'bg-orange-100 text-orange-700 border-orange-200',
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  REQUEST: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
};

const StatCard = ({ label, value, helper }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
    {helper ? <p className="mt-1 text-xs text-slate-500">{helper}</p> : null}
  </div>
);

const Badge = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${className}`}>
    {children}
  </span>
);

const CompanyEditTransactionDashboard = () => {
  const userId = localStorage.getItem('id');
  const role = localStorage.getItem('role') || '';
  const companyId = localStorage.getItem('company_id') || '';
  const username = localStorage.getItem('username') || localStorage.getItem('user_name') || 'Admin';
  const { isSidebarOpen } = useContext(SidebarContext);
  const [searchParams] = useSearchParams();
  const highlightTransactionNo = searchParams.get('transaction_no') || '';
  const highlightUserId = searchParams.get('user_id') || '';
  const highlightUserName = searchParams.get('user_name') || '';

  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState(highlightUserName);
  const [filters, setFilters] = useState({
    user: highlightUserName,
    user_id: highlightUserId,
    status: '',
    start_date: '',
    end_date: '',
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [expandedTransactionNo, setExpandedTransactionNo] = useState('');
  const [formData, setFormData] = useState(null);
  const [transactionImage, setTransactionImage] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);

  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((user) => map.set(String(user.id), user));
    return map;
  }, [users]);

  const loadUsers = async () => {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (userId) params.set('actor_id', userId);
    if (role) params.set('actor_role', role);

    const data = await fetchJson(`${ApiURL}/getUserReport.php?${params.toString()}`);
    if (!Array.isArray(data)) {
      throw new Error('Invalid user list received');
    }

    setUsers(data);
  };

  const loadProjects = async () => {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);

    const data = await fetchJson(`${ApiURL}/projects.php?${params.toString()}`);
    if (data.status !== 'success') {
      throw new Error(data.message || 'Failed to load projects');
    }

    const uniqueProjects = Array.from(
      new Map((data.projects || []).map((project) => [String(project.project_name || '').trim(), project])).values()
    ).filter((project) => String(project.project_name || '').trim() !== '');
    setProjects(uniqueProjects);
  };

  const loadTransactions = async (customFilters = filters) => {
    setIsLoading(true);
    setError('');

    try {
      const data = await fetchJson(`${ApiURL}/getEditTransactionReport.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          company_id: companyId,
          worker_name: customFilters.user,
          target_user_id: customFilters.user_id ? Number(customFilters.user_id) : 0,
          start_date: customFilters.start_date,
          end_date: customFilters.end_date,
          form_status: customFilters.status,
        }),
      });

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load transactions');
      }

      const nextTransactions = data.transactions || [];
      setTransactions(nextTransactions);
      return nextTransactions;
    } catch (loadError) {
      setError(loadError.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!['admin', 'company_owner'].includes(role)) {
      setError('Only admins and company owners can access this screen.');
      return;
    }

    loadUsers().catch((loadError) => setError(loadError.message));
    loadProjects().catch((loadError) => setError(loadError.message));
    loadTransactions();
    fetchTransactionPhotoRequired(userId).then(setPhotoRequired).catch(() => {});
  }, []);

  useEffect(() => {
    if (!highlightUserId || !users.length) return;
    const match = users.find((user) => String(user.id) === String(highlightUserId));
    if (!match) return;

    setFilters((prev) => {
      if (String(prev.user_id) === String(match.id) && prev.user === match.user_name) {
        return prev;
      }
      return {
        ...prev,
        user_id: String(match.id),
        user: match.user_name,
      };
    });
  }, [highlightUserId, users]);

  useEffect(() => {
    if (!highlightTransactionNo || !transactions.length) return;
    const match = transactions.find((txn) => String(txn.transaction_no) === String(highlightTransactionNo));
    if (match) {
      openEditor(match);
      setExpandedTransactionNo(String(match.transaction_no));
    }
  }, [highlightTransactionNo, transactions]);

  const enrichedTransactions = useMemo(() => {
    return transactions.map((txn) => {
      const user = userMap.get(String(txn.transaction_user_id));
      return {
        ...txn,
        actor_role: user?.role || 'worker',
        actor_name: user?.user_name || txn.transaction_user_name || txn.particuler_to || '-',
      };
    });
  }, [transactions, userMap]);

  const filteredTransactions = useMemo(() => {
    if (!search.trim()) return enrichedTransactions;
    const query = search.trim().toLowerCase();

    return enrichedTransactions.filter((txn) =>
      [
        txn.transaction_no,
        txn.user_transaction_no,
        txn.actor_name,
        txn.particuler_to,
        txn.site,
        txn.project_name,
        txn.main_head,
        txn.sub_head,
        txn.remarks,
        txn.created_by,
        txn.updated_by,
        txn.form_status,
      ].some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [enrichedTransactions, search]);

  const summary = useMemo(() => {
    const total = filteredTransactions.length;
    const credit = filteredTransactions.reduce((sum, txn) => sum + Number(txn.credit_amount || 0), 0);
    const debit = filteredTransactions.reduce((sum, txn) => sum + Number(txn.debit_amount || 0), 0);
    return { total, credit, debit };
  }, [filteredTransactions]);

  const openEditor = (txn) => {
    setSelectedTransaction(txn);
    setTransactionImage(null);
    setFormData({
      transaction_no: txn.transaction_no,
      balance_id: txn.balance_id || txn.transaction_no,
      transaction_user_id: txn.transaction_user_id,
      transaction_date: txn.transaction_date || '',
      amount_type: txn.amount_type || 'debit',
      creditAmount: txn.credit_amount || '0.00',
      debitAmount: txn.debit_amount || '0.00',
      netBalance: txn.net_balance || '0.00',
      remarks: txn.remarks || '',
      particuler_to: txn.particuler_to || '',
      site: txn.site || '',
      project_name: txn.project_name || '',
      main_head: txn.main_head || '',
      sub_head: txn.sub_head || '',
      form_status: txn.form_status || '',
      created_by: txn.created_by || '',
      created_at: txn.created_at || '',
      updated_by: txn.updated_by || username,
      updated_at: txn.updated_at || '',
      actor_role: txn.actor_role || 'worker',
      actor_name: txn.actor_name || '',
      source_table: txn.source_table || 'txn_book',
    });
  };

  const closeEditor = () => {
    setSelectedTransaction(null);
    setFormData(null);
    setTransactionImage(null);
    setSuccess('');
  };

  const handleFilterChange = (key, value) => {
    if (key === 'user_id') {
      const match = users.find((user) => String(user.id) === String(value));
      setFilters((prev) => ({
        ...prev,
        user_id: value,
        user: match?.user_name || '',
      }));
      return;
    }

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadTransactions(filters);
  };

  const clearFilters = () => {
    const cleared = { user: '', user_id: '', status: '', start_date: '', end_date: '' };
    setFilters(cleared);
    setSearch('');
    loadTransactions(cleared);
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!formData) return;

    const safeRemarks = String(formData.remarks || '').trim() || '-';
    if (photoRequired && !transactionImage?.dataUrl && !selectedTransaction?.attachment_path) {
      setError('Proof image is required by company setting.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = await fetchJson(`${ApiURL}/editTransaction.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_no: formData.transaction_no,
          balance_id: formData.balance_id,
          source_table: formData.source_table,
          user_id: userId,
          creditAmount: Number(formData.creditAmount || 0),
          debitAmount: Number(formData.debitAmount || 0),
          netBalance: 0,
          remarks: safeRemarks,
          updated_by: username,
          transaction_date: formData.transaction_date,
          amount_type: formData.amount_type,
          particuler_to: formData.particuler_to,
          site: formData.site,
          project_name: formData.project_name,
          main_head: formData.main_head,
          sub_head: formData.sub_head,
          form_status: formData.form_status,
          attachment_image: imagePayload(transactionImage),
        }),
      });

      if (updateData.status !== 'success') {
        throw new Error(updateData.message || 'Failed to update transaction');
      }

      await fetchJson(`${ApiURL}/logEditTransactionHistory.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_no: formData.transaction_no,
          balance_id: formData.balance_id,
          source_table: formData.source_table,
          user_id: formData.transaction_user_id,
          creditAmount: Number(formData.creditAmount || 0),
          debitAmount: Number(formData.debitAmount || 0),
          netBalance: 0,
          remarks: safeRemarks,
          updated_by: username,
          transaction_date: formData.transaction_date,
          amount_type: formData.amount_type,
          particuler_to: formData.particuler_to,
          site: formData.site,
          project_name: formData.project_name,
          main_head: formData.main_head,
          sub_head: formData.sub_head,
          form_status: formData.form_status,
          original: selectedTransaction,
        }),
      });

      setSuccess('Transaction updated successfully.');
      const refreshedTransactions = await loadTransactions(filters);
      const refreshedMatch = refreshedTransactions.find(
        (txn) => String(txn.transaction_no) === String(formData.transaction_no)
      );
      if (refreshedMatch) {
        openEditor(refreshedMatch);
      }
      setTransactionImage(null);
      setExpandedTransactionNo(String(formData.transaction_no));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`min-w-0 flex-1 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-panel">
              <div className="h-[3px] bg-brand-600" />
              <div className="p-5 sm:p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">Company Edit Center</p>
                  <h1 className="mt-2 text-2xl font-bold text-slate-950 sm:text-3xl">Edit Transactions Across Your Company</h1>
                  <p className="mt-2 max-w-3xl text-sm text-slate-500">
                    Review admin, worker, and company-owner transactions in one place, then open a record to compare the current values with the previous transaction history before saving.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => loadTransactions(filters)}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <StatCard label="Visible Transactions" value={summary.total} helper="Filtered company records" />
              <StatCard label="Total Credit" value={formatCurrency(summary.credit)} helper="Current filtered credit amount" />
              <StatCard label="Total Debit" value={formatCurrency(summary.debit)} helper="Current filtered debit amount" />
            </div>

            <div className="mt-5 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">User</label>
                    <select
                      value={filters.user_id}
                      onChange={(event) => handleFilterChange('user_id', event.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                    >
                      <option value="">All Company Users</option>
                      {[...users]
                        .sort((a, b) => a.user_name.localeCompare(b.user_name))
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.user_name} ({user.role})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Search</label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Txn no, site, remarks, user..."
                        className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">From</label>
                    <div className="relative">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        type="date"
                        value={filters.start_date}
                        onChange={(event) => handleFilterChange('start_date', event.target.value)}
                        className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">To</label>
                    <div className="relative">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        type="date"
                        value={filters.end_date}
                        onChange={(event) => handleFilterChange('end_date', event.target.value)}
                        className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Status</label>
                    <select
                      value={filters.status}
                      onChange={(event) => handleFilterChange('status', event.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                    >
                      <option value="">All Statuses</option>
                      {['SUBMIT', 'RECEIVED', 'RETURN', 'APPROVED', 'REQUEST'].map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={applyFilters}
                      className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                  >
                    <FunnelIcon className="h-4 w-4" />
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {error ? (
              <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {error}
              </div>
            ) : null}

            {success ? (
              <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {success}
              </div>
            ) : null}

            <div className="mt-5 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[1460px] w-full text-left text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      {['Txn', 'Role', 'User', 'Date', 'Type', 'Credit', 'Debit', 'Balance', 'Site', 'Project', 'Heads', 'Proof', 'Status', 'Updated', 'Action'].map((heading) => (
                        <th key={heading} className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="15" className="px-4 py-12 text-center text-sm font-medium text-slate-500">
                          Loading transactions...
                        </td>
                      </tr>
                    ) : filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="15" className="px-4 py-12 text-center text-sm font-medium text-slate-500">
                          No company transactions found for the current filters.
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map((txn) => {
                        const roleName = (txn.actor_role || 'worker').toLowerCase();
                        const normalizedStatus = String(txn.form_status || '').toUpperCase();
                        const normalizedType = String(txn.amount_type || '').toLowerCase();
                        const isHighlighted = String(txn.transaction_no) === String(highlightTransactionNo);
                        return (
                          <React.Fragment key={txn.transaction_no}>
                            <tr className={`border-b border-slate-100 transition hover:bg-slate-50 ${isHighlighted ? 'bg-brand-50' : ''}`}>
                              <td className="px-4 py-3 font-bold text-slate-900">#{txn.transaction_no}</td>
                              <td className="px-4 py-3">
                                <Badge className={roleStyles[roleName] || roleStyles.worker}>
                                  {roleName.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-900">{txn.actor_name}</span>
                                  <span className="text-xs text-slate-500">{txn.particuler_to || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-700">{formatDate(txn.transaction_date)}</td>
                              <td className="px-4 py-3">
                                <Badge className={typeStyles[normalizedType] || typeStyles.debit}>{normalizedType || 'debit'}</Badge>
                              </td>
                              <td className="px-4 py-3 font-semibold text-emerald-700">{formatCurrency(txn.credit_amount)}</td>
                              <td className="px-4 py-3 font-semibold text-rose-700">{formatCurrency(txn.debit_amount)}</td>
                              <td className="px-4 py-3 font-bold text-slate-900">{formatCurrency(txn.net_balance)}</td>
                              <td className="px-4 py-3 text-slate-700">{txn.site || '-'}</td>
                              <td className="px-4 py-3 text-slate-700">{txn.project_name || '-'}</td>
                              <td className="px-4 py-3 text-slate-700">
                                <div className="flex flex-col">
                                  <span>{txn.main_head || '-'}</span>
                                  <span className="text-xs text-slate-500">{txn.sub_head || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3"><TransactionProofImage path={txn.attachment_path} /></td>
                              <td className="px-4 py-3">
                                <Badge className={statusStyles[normalizedStatus] || statusStyles.RECEIVED}>{normalizedStatus || '-'}</Badge>
                              </td>
                              <td className="px-4 py-3 text-xs text-slate-600">
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-800">{txn.updated_by || txn.created_by || '-'}</span>
                                  <span>{formatDateTime(txn.updated_at || txn.created_at)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openEditor(txn)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-brand-700"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedTransactionNo((prev) =>
                                        prev === String(txn.transaction_no) ? '' : String(txn.transaction_no)
                                      )
                                    }
                                    className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                  >
                                    <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                                    History
                                  </button>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan="15" className="bg-slate-50/50 px-4">
                                <TransactionHistoryReport
                                  transactionNo={txn.transaction_no}
                                  userId={txn.transaction_user_id}
                                  isOpen={expandedTransactionNo === String(txn.transaction_no)}
                                  onClose={() => setExpandedTransactionNo('')}
                                />
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      <AnimatePresence>
        {selectedTransaction && formData ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/65 p-2 backdrop-blur-sm sm:p-4"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeEditor();
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              className="flex h-[calc(100vh-1rem)] w-full max-w-[1180px] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:h-[calc(100vh-2rem)]"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5 sm:py-4">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-600">Edit Transaction</p>
                  <h2 className="mt-1 truncate text-lg font-bold text-slate-900 sm:text-xl">Transaction #{formData.transaction_no}</h2>
                </div>
                <button
                  type="button"
                  onClick={closeEditor}
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="grid min-h-0 flex-1 overflow-hidden bg-white lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
                <div className="grid min-w-0 content-start gap-4 overflow-y-auto border-slate-200 bg-white p-4 sm:p-5 lg:border-r">
                  <form id="edit-transaction-form" className="order-1 grid gap-3 sm:grid-cols-2" onSubmit={submitEdit}>
                    {[
                      ['transaction_date', 'Date', 'date'],
                      ['particuler_to', 'Particular To', 'text'],
                      ['site', 'Site', 'text'],
                      ['creditAmount', 'Credit Amount', 'number'],
                      ['debitAmount', 'Debit Amount', 'number'],
                    ].map(([key, label, type]) => (
                      <div key={key}>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</label>
                        <input
                          type={type}
                          step={type === 'number' ? '0.01' : undefined}
                          min={type === 'number' ? '0' : undefined}
                          value={formData[key]}
                          onChange={(event) => setFormData((prev) => ({ ...prev, [key]: event.target.value }))}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                        />
                      </div>
                    ))}

                    <div>
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Project</label>
                      <select
                        value={formData.project_name}
                        onChange={(event) => setFormData((prev) => ({ ...prev, project_name: event.target.value }))}
                        className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      >
                        <option value="">No project</option>
                        {projects.map((project) => (
                          <option key={project.project_id || project.project_name} value={project.project_name}>
                            {project.project_name}
                          </option>
                        ))}
                        {formData.project_name && !projects.some((project) => project.project_name === formData.project_name) ? (
                          <option value={formData.project_name}>{formData.project_name}</option>
                        ) : null}
                      </select>
                    </div>

                    {[
                      ['main_head', 'Main Head'],
                      ['sub_head', 'Sub Head'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</label>
                        <input
                          type="text"
                          value={formData[key]}
                          onChange={(event) => setFormData((prev) => ({ ...prev, [key]: event.target.value }))}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                        />
                      </div>
                    ))}

                    <div>
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Type</label>
                      <select
                        value={formData.amount_type}
                        onChange={(event) => setFormData((prev) => ({ ...prev, amount_type: event.target.value }))}
                        className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      >
                        <option value="credit">credit</option>
                        <option value="debit">debit</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Status</label>
                      <select
                        value={formData.form_status}
                        onChange={(event) => setFormData((prev) => ({ ...prev, form_status: event.target.value }))}
                        className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      >
                        {['SAVE', 'SUBMIT', 'RECEIVED', 'RETURN', 'APPROVED', 'REQUEST'].map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Remarks</label>
                      <textarea
                        value={formData.remarks}
                        onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
                        rows={3}
                        placeholder="Add remarks for this edit"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <TransactionImageField
                        value={transactionImage}
                        onChange={setTransactionImage}
                        required={photoRequired && !selectedTransaction?.attachment_path}
                        label="Updated proof image"
                      />
                      {selectedTransaction?.attachment_path ? (
                        <p className="mt-2 text-xs font-medium text-slate-500">
                          Existing proof is saved. Upload a new image only if this edit needs a replacement.
                        </p>
                      ) : null}
                    </div>

                  </form>

                  <div className="order-2 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Transaction Owner</p>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="rounded-lg bg-brand-100 p-2 text-brand-700">
                          {formData.actor_role === 'worker' ? <UserGroupIcon className="h-5 w-5" /> : <ShieldCheckIcon className="h-5 w-5" />}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{formData.actor_name || '-'}</p>
                          <Badge className={roleStyles[formData.actor_role] || roleStyles.worker}>{formData.actor_role.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Audit Snapshot</p>
                      <div className="mt-3 space-y-2 text-sm text-slate-600">
                        <p><span className="font-semibold text-slate-800">Created By:</span> {formData.created_by || '-'}</p>
                        <p><span className="font-semibold text-slate-800">Created At:</span> {formatDateTime(formData.created_at)}</p>
                        <p><span className="font-semibold text-slate-800">Last Updated By:</span> {selectedTransaction.updated_by || '-'}</p>
                        <p><span className="font-semibold text-slate-800">Last Updated At:</span> {formatDateTime(selectedTransaction.updated_at)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="order-3 rounded-xl border border-brand-200 bg-brand-50 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-700">Previous Transaction Snapshot</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ['Date', formatDate(selectedTransaction.transaction_date)],
                        ['Type', selectedTransaction.amount_type],
                        ['Credit', formatCurrency(selectedTransaction.credit_amount)],
                        ['Debit', formatCurrency(selectedTransaction.debit_amount)],
                        ['Balance', formatCurrency(selectedTransaction.net_balance)],
                        ['Status', selectedTransaction.form_status || '-'],
                        ['Particular To', selectedTransaction.particuler_to || '-'],
                        ['Site', selectedTransaction.site || '-'],
                        ['Project', selectedTransaction.project_name || '-'],
                        ['Main Head', selectedTransaction.main_head || '-'],
                        ['Sub Head', selectedTransaction.sub_head || '-'],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600">{label}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-800">{value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600">Remarks</p>
                      <p className="mt-1 text-sm text-slate-700">{selectedTransaction.remarks || '-'}</p>
                    </div>
                    <div className="mt-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600">Proof Image</p>
                      <div className="mt-1"><TransactionProofImage path={selectedTransaction.attachment_path} /></div>
                    </div>
                  </div>

                </div>

                <div className="min-w-0 overflow-y-auto bg-slate-50 p-4 sm:p-5">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">History Viewer</p>
                        <h3 className="mt-1 text-xl font-bold text-slate-900">Old vs Updated Record Trail</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          This section shows the original transaction record and the latest updated version stored for this transaction number.
                        </p>
                      </div>
                      <div className="rounded-lg bg-brand-100 p-2 text-brand-700">
                        <DocumentMagnifyingGlassIcon className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <TransactionHistoryReport
                        transactionNo={formData.transaction_no}
                        userId={formData.transaction_user_id}
                        isOpen
                        onClose={() => {}}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:justify-end sm:px-5">
                <button
                  type="button"
                  onClick={closeEditor}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:flex-none sm:min-w-32"
                >
                  Close
                </button>
                <button
                  type="submit"
                  form="edit-transaction-form"
                  disabled={isSaving}
                  className="flex-1 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:min-w-44"
                >
                  {isSaving ? 'Saving...' : 'Save Transaction'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default CompanyEditTransactionDashboard;
